import type { SupabaseClient } from '@supabase/supabase-js'

export const passwordAuthOptions = {
  persistSession: false,
  autoRefreshToken: false,
  detectSessionInUrl: false,
}
export const invalidPasswordLink = 'This link has expired or is invalid. Request a fresh password-setup or reset email below.'

export async function requestPasswordLink(auth: SupabaseClient['auth'], email: string, origin: string) {
  const address = email.trim().toLowerCase()
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) throw new Error('Enter your account email address.')
  const allowedOrigins = ['https://ourrenland.com', 'https://www.ourrenland.com', 'http://127.0.0.1:5173', 'http://localhost:5173']
  if (!allowedOrigins.includes(origin)) throw new Error('Use the guild website to request a password link.')
  const { error } = await auth.resetPasswordForEmail(address, { redirectTo: origin + '/account-password' })
  if (error) throw error
}
export function readPasswordLink(fragment: string) {
  const params = new URLSearchParams(fragment.replace(/^#/, ''))
  const hash = params.get('token_hash')
  const type = params.get('type')
  return hash && /^\w+$/.test(hash) && (type === 'invite' || type === 'recovery') ? { hash, type } : null
}

// Only an explicitly verified email link authorizes this isolated flow. An
// existing portal session, including an administrator's, is never authority.
export class PasswordFlow {
  private auth: SupabaseClient['auth']
  private target: { id: string; email: string } | null = null
  constructor(auth: SupabaseClient['auth']) { this.auth = auth }
  async verify(link: ReturnType<typeof readPasswordLink>) {
    this.target = null
    if (!link) throw new Error(invalidPasswordLink)
    const { data, error } = await this.auth.verifyOtp({ token_hash: link.hash, type: link.type as 'invite' | 'recovery' })
    if (error || !data.session || !data.user?.email || data.session.user.id !== data.user.id) throw new Error(invalidPasswordLink)
    this.target = { id: data.user.id, email: data.user.email }
    return this.target
  }
  async save(password: string) {
    if (!this.target) throw new Error(invalidPasswordLink)
    const { data, error } = await this.auth.getUser()
    if (error || data.user?.id !== this.target.id || data.user?.email !== this.target.email) {
      this.target = null
      throw new Error(invalidPasswordLink)
    }
    const result = await this.auth.updateUser({ password })
    if (result.error) throw result.error
    this.target = null
    // Local scope ends only this email-link session, not other signed-in devices.
    await this.auth.signOut({ scope: 'local' })
  }
}
